import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  UserPlusIcon,
  UserIcon,
  ShieldCheckIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckIcon,
  XMarkIcon,
  TrashIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

function UserManagement() {
  const { currentUser, hasPermission } = useAuth();
  const [users, setUsers] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [userForm, setUserForm] = useState({
    email: '',
    displayName: '',
    role: 'sales_manager',
    permissions: {
      allowPurchases: false,
      allowSuppliers: false,
      allowReports: false,
      allowInventory: false,
      allowDelete: false
    }
  });

  // Only owner can access this page
  if (!hasPermission('manage_users')) {
    return (
      <div className="text-center py-12">
        <ShieldCheckIcon className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Access Denied</h3>
        <p className="mt-1 text-sm text-gray-500">Only the owner can access user management.</p>
      </div>
    );
  }

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      // Load users from localStorage (you can also load from Firebase)
      const savedUsers = localStorage.getItem('systemUsers');
      if (savedUsers) {
        setUsers(JSON.parse(savedUsers));
      }
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveUsers = async (usersList) => {
    try {
      localStorage.setItem('systemUsers', JSON.stringify(usersList));
      setUsers(usersList);
    } catch (error) {
      console.error('Error saving users:', error);
      throw error;
    }
  };

  const handleCreateUser = async () => {
    if (!userForm.email || !userForm.displayName) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      
      const newUser = {
        id: Date.now().toString(),
        email: userForm.email,
        displayName: userForm.displayName,
        role: userForm.role,
        permissions: userForm.permissions,
        createdAt: new Date().toISOString(),
        createdBy: currentUser.email,
        isActive: true
      };

      const updatedUsers = [...users, newUser];
      await saveUsers(updatedUsers);
      
      toast.success('User created successfully!');
      setShowCreateForm(false);
      resetForm();
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error('Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateUser = async () => {
    try {
      setSaving(true);
      
      const updatedUsers = users.map(user => 
        user.id === editingUser.id 
          ? { ...user, ...userForm, updatedAt: new Date().toISOString() }
          : user
      );
      
      await saveUsers(updatedUsers);
      
      toast.success('User updated successfully!');
      setEditingUser(null);
      resetForm();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      const updatedUsers = users.filter(user => user.id !== userId);
      await saveUsers(updatedUsers);
      toast.success('User deleted successfully!');
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    }
  };

  const resetForm = () => {
    setUserForm({
      email: '',
      displayName: '',
      role: 'sales_manager',
      permissions: {
        allowPurchases: false,
        allowSuppliers: false,
        allowReports: false,
        allowInventory: false,
        allowDelete: false
      }
    });
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserForm({
      email: user.email,
      displayName: user.displayName,
      role: user.role,
      permissions: user.permissions
    });
    setShowCreateForm(true);
  };

  const handlePermissionChange = (permission, value) => {
    setUserForm(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [permission]: value
      }
    }));
  };

  const permissionItems = [
    { key: 'allowPurchases', title: 'Purchase Management', icon: '🛒' },
    { key: 'allowSuppliers', title: 'Supplier Management', icon: '🏢' },
    { key: 'allowReports', title: 'Reports Access', icon: '📊' },
    { key: 'allowInventory', title: 'Inventory Access', icon: '📦' },
    { key: 'allowDelete', title: 'Record Deletion', icon: '🗑️' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            User Management
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Create and manage user accounts and permissions
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <button
            onClick={() => {
              resetForm();
              setEditingUser(null);
              setShowCreateForm(true);
            }}
            className="btn-primary"
          >
            <UserPlusIcon className="h-4 w-4 mr-2" />
            Create User
          </button>
        </div>
      </div>

      {/* Create/Edit User Form */}
      {showCreateForm && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900">
              {editingUser ? 'Edit User' : 'Create New User'}
            </h3>
            <button
              onClick={() => {
                setShowCreateForm(false);
                setEditingUser(null);
                resetForm();
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address *
              </label>
              <input
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                className="input"
                placeholder="user@a2ztak.in"
                disabled={editingUser}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Display Name *
              </label>
              <input
                type="text"
                value={userForm.displayName}
                onChange={(e) => setUserForm({ ...userForm, displayName: e.target.value })}
                className="input"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role
              </label>
              <select
                value={userForm.role}
                onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                className="input"
              >
                <option value="sales_manager">Sales Manager</option>
                <option value="staff">Staff</option>
              </select>
            </div>
          </div>

          {/* Permissions */}
          <div className="mt-6">
            <h4 className="text-sm font-medium text-gray-900 mb-4">Permissions</h4>
            <div className="space-y-3">
              {permissionItems.map((item) => (
                <div key={item.key} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div className="flex items-center">
                    <span className="text-xl mr-3">{item.icon}</span>
                    <span className="text-sm font-medium text-gray-900">{item.title}</span>
                  </div>
                  <button
                    onClick={() => handlePermissionChange(item.key, !userForm.permissions[item.key])}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                      userForm.permissions[item.key] ? 'bg-indigo-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        userForm.permissions[item.key] ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={() => {
                setShowCreateForm(false);
                setEditingUser(null);
                resetForm();
              }}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={editingUser ? handleUpdateUser : handleCreateUser}
              disabled={saving}
              className="btn-primary"
            >
              {saving ? (
                <div className="spinner mr-2"></div>
              ) : (
                <CheckIcon className="h-4 w-4 mr-2" />
              )}
              {editingUser ? 'Update User' : 'Create User'}
            </button>
          </div>
        </div>
      )}

      {/* Users List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">System Users</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            {users.length} user{users.length !== 1 ? 's' : ''} found
          </p>
        </div>
        
        {users.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table">
              <thead className="table-header">
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Permissions</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody className="table-body">
                {users.map((user) => (
                  <tr key={user.id}>
                    <td className="font-medium">{user.displayName}</td>
                    <td>{user.email}</td>
                    <td>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {user.role.replace('_', ' ')}
                      </span>
                    </td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(user.permissions).map(([key, value]) => (
                          value && (
                            <span key={key} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {permissionItems.find(p => p.key === key)?.icon} {permissionItems.find(p => p.key === key)?.title.split(' ')[0]}
                            </span>
                          )
                        ))}
                      </div>
                    </td>
                    <td>
                      {new Date(user.createdAt).toLocaleDateString('en-IN')}
                    </td>
                    <td>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEditUser(user)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No users</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by creating your first user.</p>
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <UserIcon className="h-5 w-5 text-blue-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Next Steps</h3>
            <div className="mt-2 text-sm text-blue-700">
              <ol className="list-decimal list-inside space-y-1">
                <li>Create user accounts here</li>
                <li>Create corresponding Firebase accounts in Firebase Console</li>
                <li>Users can login with their email and Firebase password</li>
                <li>Permissions are applied automatically based on their role</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserManagement;
